import {
  Button,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormNotice,
  FormRow,
  FormTitle,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import { NavButton } from "@src/components/navButton/NavButton";
import {
  Form,
  InputCheckbox,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import { Stepbar } from "@src/components/stepbar/Stepbar";
import { environment } from "@src/MarqueBlanche";
import "./consentSouscription.scss";
import { MicroStep, StepProps } from "@src/Steps/step/Steps";

export type ConsentSouscriptionData =
  | {
      consentDocInfoProduit: boolean;
      consentExclusionReg: boolean;
      consentFausseDec: boolean;
      consentRenoncerContrat: boolean;
      consentResiliation: boolean;
      consentVeracite: boolean;
      consentTermesProduit: boolean;
      consentContact?: boolean;
    }
  | undefined;

type ConsentSouscriptionProps = StepProps<ConsentSouscriptionData>;

export const CONSENTEMENT_PRODUIT =
  "Vous reconnaissez avoir pris connaissance du document d'information sur le produit d'assurance relatif au contrat Pavixie, du règlement du contrat Pavixie et de ses annexes, de la notice d'information relative à l'assistance RMA ainsi que des statuts de Tutélaire.";
export const CONSENTEMENT_EXCLUSIONS_PRODUIT =
  "Vous reconnaissez avoir pris connaissance des exclusions figurant à l&apos;article 16 du règlement du contrat Pavixie.";
export const CONSENTEMENT_FAUSSE_DECLA =
  "Vous reconnaissez avoir pris connaissance des dispositions relatives à la fausse déclaration et à ses conséquences, telles qu&apos;énoncées à l&apos;article 14 du règlement du contrat Paxivie.";
export const CONSENTEMENT_RENONCER_CONTRAT =
  "Vous reconnaissez avoir été informé(e) que vous bénéficiez de la faculté de renoncer à votre contrat Paxivie dans un délai de 30 jours calendaires suivants sa date de prise d&apos;effet dont les modalités sont prévues à l&apos;article 6 du règlement du contrat Paxivie.";
export const CONSENTEMENT_RESILIATION =
  "Vous reconnaissez avoir été informé(e) de votre droit de résilier à tout moment votre contrat Paxivie indépendamment de la date d&apos;envoi de chaque avis d&apos;échéance annuel de cotisation en adressant par lettre, par voie électronique ou tout autre support durable, votre demande au siège de la mutuelle.";
export const DECLARATION_VERACITE =
  "Vous déclarez exact l&apos;ensemble des informations renseignées.";
export const CONSENTEMENT_REGLEMENT_CG =
  "Vous acceptez les termes du règlement du contrat pavixie et de ses annexes";
export const CONSENTEMENT_CONTACT =
  "Vous acceptez d&apos;être contacté dans le cadre du développement des offres et des services de la mutuelle Tutélaire, afin de recevoir des informations portant sur les contrats ou la vie de la mutuelle.";

const schema = zod.object({
  consentDocInfoProduit: zod
    .boolean({ required_error: "Champ requis" })
    .refine((val) => val, "Champ requis"),
  consentExclusionReg: zod
    .boolean({ required_error: "Champ requis" })
    .refine((val) => val, "Champ requis"),
  consentFausseDec: zod
    .boolean({ required_error: "Champ requis" })
    .refine((val) => val, "Champ requis"),
  consentRenoncerContrat: zod
    .boolean({ required_error: "Champ requis" })
    .refine((val) => val, "Champ requis"),
  consentResiliation: zod
    .boolean({ required_error: "Champ requis" })
    .refine((val) => val, "Champ requis"),
  consentVeracite: zod
    .boolean({ required_error: "Champ requis" })
    .refine((val) => val, "Champ requis"),
  consentTermesProduit: zod
    .boolean({ required_error: "Champ requis" })
    .refine((val) => val, "Champ requis"),
  consentContact: zod.boolean().optional(),
});
type FormValues = zod.infer<typeof schema>;

export function ConsentSouscription({
  changeStep,
  data,
  setData,
}: ConsentSouscriptionProps) {
  const formConfig = useFormConfig<typeof schema, FormValues>({
    schema,
    defaultValues: data,
  });

  const handleSubmit = (submittedData: FormValues) => {
    setData(submittedData);
    changeStep("MANDAT_PRELEVEMENT", "forward");
  };

  return (
    <div className="consent-souscription-step">
      <div className="stepper__centered-container">
        <div className="stepper__centered-content step__grid step__grid--padding-bottom">
          <Stepbar activeMicroStep={MicroStep.PersonalInformation} />
          <NavButton
            onClick={() => {
              changeStep("BENEFICIAIRES", "backward");
            }}
          >
            Précédent
          </NavButton>

          <Form formConfig={formConfig} onSubmit={handleSubmit}>
            <FormGrid>
              <FormTitle>
                <h1>
                  La conclusion de votre contrat Paxivie sera effective si vous
                  êtes en mesure de valider les conditions suivantes :
                </h1>
              </FormTitle>
              <FormNotice>
                <p>
                  <Emphasis>*</Emphasis> Mentions obligatoires
                </p>
              </FormNotice>
              <FormRow>
                <FormField>
                  <InputCheckbox<FormValues>
                    name="consentDocInfoProduit"
                    required
                    label={
                      <>
                        Vous reconnaissez avoir pris connaissance du{" "}
                        <a
                          tabIndex={-1}
                          target="_blank"
                          href={environment.current.docDIPA}
                          rel="noreferrer"
                        >
                          document d&apos;information sur le produit
                          d&apos;assurance
                        </a>{" "}
                        relatif au contrat Pavixie, du{" "}
                        <a
                          tabIndex={-1}
                          target="_blank"
                          href={environment.current.docReglement}
                          rel="noreferrer"
                        >
                          règlement
                        </a>{" "}
                        du contrat Pavixie et de ses annexes, de la{" "}
                        <a
                          tabIndex={-1}
                          target="_blank"
                          href={environment.current.docAssistance}
                          rel="noreferrer"
                        >
                          notice d&apos;information relative à l&apos;assistance
                          RMA
                        </a>{" "}
                        ainsi que des{" "}
                        <a
                          tabIndex={-1}
                          target="_blank"
                          href={environment.current.docStatut}
                          rel="noreferrer"
                        >
                          statuts
                        </a>{" "}
                        de Tutélaire
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<FormValues>
                    name="consentExclusionReg"
                    required
                    label={
                      <>
                        Vous reconnaissez avoir pris connaissance des exclusions
                        figurant à l&apos;article 16 du{" "}
                        <a
                          tabIndex={-1}
                          target="_blank"
                          href={environment.current.docReglement}
                          rel="noreferrer"
                        >
                          règlement
                        </a>{" "}
                        du contrat Pavixie
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<FormValues>
                    name="consentFausseDec"
                    required
                    label={
                      <>
                        Vous reconnaissez avoir pris connaissance des
                        dispositions relatives à la fausse déclaration et à ses
                        conséquences, telles qu&apos;énoncées à l&apos;article
                        14 du{" "}
                        <a
                          tabIndex={-1}
                          target="_blank"
                          href={environment.current.docReglement}
                          rel="noreferrer"
                        >
                          règlement
                        </a>{" "}
                        du contrat Pavixie
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<FormValues>
                    name="consentRenoncerContrat"
                    required
                    label={
                      <>
                        Vous reconnaissez avoir été informé(e) que vous
                        bénéficiez de la faculté de renoncer à votre contrat
                        Paxivie dans un délai de 30 jours calendaires suivants
                        sa date de prise d&apos;effet dont les modalités sont
                        prévues à l&apos;article 6 du{" "}
                        <a
                          tabIndex={-1}
                          target="_blank"
                          href={environment.current.docReglement}
                          rel="noreferrer"
                        >
                          règlement
                        </a>{" "}
                        du contrat Pavixie
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<FormValues>
                    name="consentResiliation"
                    required
                    label={
                      <>
                        Vous reconnaissez avoir été informé(e) de votre droit de
                        résilier à tout moment votre contrat Paxivie
                        indépendamment de la date d&apos;envoi de chaque avis
                        d&apos;échéance annuel de cotisation en adressant par
                        lettre, par voie électronique ou tout autre support
                        durable, votre demande au siège de la mutuelle
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<FormValues>
                    name="consentVeracite"
                    required
                    label={
                      <>
                        Vous déclarez exact l’ensemble des informations
                        renseignées
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<FormValues>
                    name="consentTermesProduit"
                    required
                    label={
                      <>
                        Vous acceptez les termes du{" "}
                        <a
                          tabIndex={-1}
                          target="_blank"
                          href={environment.current.docReglement}
                          rel="noreferrer"
                        >
                          règlement
                        </a>{" "}
                        du contrat pavixie et de ses annexes
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<FormValues>
                    name="consentContact"
                    label={
                      <>
                        Vous acceptez d’être contacté dans le cadre du
                        développement des offres et des services de la mutuelle
                        Tutélaire, afin de recevoir des informations portant sur
                        les contrats ou la vie de la mutuelle.
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              <FormAction>
                <Button
                  type="submit"
                  disabled={
                    !formConfig.formState.isValid ||
                    formConfig.formState.isSubmitting ||
                    formConfig.formState.isSubmitSuccessful
                  }
                >
                  Suivant
                </Button>
              </FormAction>
            </FormGrid>
          </Form>
        </div>
      </div>
    </div>
  );
}
